import React, { useState, useEffect } from "react";
import { BookingHeader } from "./booking/BookingHeader";
import { BookingCalendar } from "./booking/BookingCalendar";
import { BookingRules } from "./booking/BookingRules";
import { BookingOptions } from "./booking/BookingOptions";
import { BookingSummary } from "./booking/BookingSummary";
import { BookingForm } from "./booking/BookingForm";
import { TimeoutModal } from "./booking/TimeoutModal";
import { $api } from "../http";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [step, setStep] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [selectedDuration, setSelectedDuration] = useState<number>(120);
  const [selectedGuests, setSelectedGuests] = useState<number>(2);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    comment: "",
    agreeTerms: false,
    agreeMarketing: false,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState<number>(today.getMonth());
  const [currentYear, setCurrentYear] = useState<number>(today.getFullYear());

  const [timeLeft, setTimeLeft] = useState<number>(600);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);

  // Prevent background scrolling when modal is active
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Session timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen && step > 1 && timeLeft > 0 && !isSuccess) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsTimeout(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOpen, step, timeLeft, isSuccess]);

  const handleResetAndClose = () => {
    setStep(1);
    setSelectedDate("");
    setSelectedTime("");
    setTimeLeft(600);
    setIsTimeout(false);
    setIsSuccess(false);
    setErrorMessage(null);
    setFormData({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      comment: "",
      agreeTerms: false,
      agreeMarketing: false,
    });
    onClose();
  };

  const generateTimeSlots = () => {
    const slots = [];
    const openMinutes = 12 * 60; // 12:00 -> 600 min Opening time
    const closeMinutes = 22 * 60; // 22:00 -> 1260 min Closed time

    const now = new Date();
    // En: Format the selected date as YYYY-MM-DD for comparison with today's date
    const todayString = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const isToday = selectedDate === todayString;
    // EN: If the selected date is today, add a 30-minute buffer to the current time to prevent booking in the past
    const currentTotalMinutes = isToday
      ? now.getHours() * 60 + now.getMinutes() + 30
      : 0;

    for (let h = 12; h < 22; h++) {
      for (let m of [0, 30]) {
        const startMinutes = h * 60 + m;

        // EN: Ensure that the booking does not exceed the restaurant's closing time
        if (
          (!isToday || startMinutes >= currentTotalMinutes) &&
          startMinutes >= openMinutes &&
          startMinutes + selectedDuration <= closeMinutes
        ) {
          const timeString = `${h.toString().padStart(2, "0")}:${
            m === 0 ? "00" : "30"
          }`;
          slots.push(timeString);
        }
      }
    }
    return slots;
  };

  // --- API CALL TO CREATE BOOKING ---
  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.agreeTerms) {
      setErrorMessage("Musisz zaakceptować regulamin, aby złożyć rezerwację.");
      return;
    }

    setIsLoading(true);

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      bookingDate: selectedDate, // YYYY-MM-DD
      bookingTime: selectedTime, // HH:mm
      durationMinutes: Number(selectedDuration),
      guestsCount: Number(selectedGuests),
      comment: formData.comment || undefined,
      agreeTerms: Boolean(formData.agreeTerms),
      agreeMarketing: Boolean(formData.agreeMarketing),
    };

    try {
      await $api.post("/booking", payload);

      setIsSuccess(true);
    } catch (err: any) {
      // EN: Extract error message from Axios response or fallback to default error
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Nie udało się połączyć z serwerem. Spróbuj ponownie.";

      setErrorMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;
  if (isTimeout) return <TimeoutModal onReset={handleResetAndClose} />;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-3 sm:p-6 overflow-hidden">
      <div className="bg-stone-900 border border-amber-500/30 text-stone-100 rounded-3xl max-w-xl w-full shadow-2xl overflow-hidden relative my-auto transition-all duration-300">
        <BookingHeader
          step={step}
          timeLeft={timeLeft}
          onClose={handleResetAndClose}
        />
        <div className="max-h-[80vh] overflow-y-auto custom-scrollbar p-4 sm:p-6">
          {/* SUCCESS SCREEN */}
          {isSuccess ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center mx-auto border border-amber-500/40">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="text-2xl font-serif font-bold text-amber-400">
                Zgłoszenie Rezerwacji Przyjęte!
              </h3>
              <p className="text-stone-300 text-sm max-w-md mx-auto">
                Dziękujemy,{" "}
                <span className="font-semibold text-stone-100">
                  {formData.firstName}
                </span>
                . Informacje o Twoim zgłoszeniu zostały wysłane na adres{" "}
                <span className="text-amber-300">{formData.email}</span>.
                Poinformujemy Cię mailowo po potwierdzeniu stolika.
              </p>

              <div className="bg-stone-950/60 p-4 rounded-xl border border-stone-800 text-left text-xs space-y-1.5 max-w-md mx-auto mt-4 text-stone-300">
                <p>
                  🗓 <strong>Data:</strong> {selectedDate}
                </p>
                <p>
                  ⏰ <strong>Godzina:</strong> {selectedTime} (
                  {selectedDuration / 60} godz.)
                </p>
                <p>
                  👥 <strong>Liczba gości:</strong> {selectedGuests}
                </p>
              </div>
              <button
                onClick={handleResetAndClose}
                className="mt-6 px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-serif font-semibold rounded-xl shadow-lg transition"
              >
                Zamknij
              </button>
            </div>
          ) : (
            <>
              {/* ERROR MESSAGE DISPLAY */}
              {errorMessage && (
                <div className="mb-4 p-3 bg-rose-950/40 border border-rose-500/50 rounded-xl text-rose-300 text-xs flex items-center gap-2">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{errorMessage}</span>
                </div>
              )}

              {step === 1 && (
                <BookingCalendar
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  today={today}
                  onPrevMonth={() =>
                    setCurrentMonth((prev) => (prev === 0 ? 11 : prev - 1))
                  }
                  onNextMonth={() =>
                    setCurrentMonth((prev) => (prev === 11 ? 0 : prev + 1))
                  }
                  onSelectDate={(date) => {
                    setSelectedDate(date);
                    setStep(2);
                  }}
                />
              )}

              {step === 2 && (
                <BookingRules
                  selectedDate={selectedDate}
                  onChangeDate={() => setStep(1)}
                  onNext={() => setStep(3)}
                />
              )}

              {[3, 4, 5].includes(step) && (
                <BookingOptions
                  step={step}
                  selectedDate={selectedDate}
                  timeSlots={generateTimeSlots()}
                  selectedTime={selectedTime}
                  selectedDuration={selectedDuration}
                  selectedGuests={selectedGuests}
                  onSelectTime={(time) => {
                    setSelectedTime(time);
                    setStep(4);
                  }}
                  onSelectDuration={(duration) => {
                    setSelectedDuration(duration);
                    setStep(5);
                  }}
                  onSelectGuests={(guests) => {
                    setSelectedGuests(guests);
                    setStep(6);
                  }}
                />
              )}

              {step === 6 && (
                <BookingSummary
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  selectedDuration={selectedDuration}
                  selectedGuests={selectedGuests}
                  onGoToStep={(s) => setStep(s)}
                  onConfirm={() => setStep(7)}
                />
              )}

              {step === 7 && (
                <BookingForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleBookingSubmit}
                  isLoading={isLoading}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
